import { useEffect, useRef, type ReactNode } from "react";
import { NavLink, useLocation, useNavigate } from "react-router";
import { XIcon, type Icon } from "@phosphor-icons/react";
import { btnClass, Button } from "../components/Button";
import { useDocumentTitle } from "../lib/useDocumentTitle";

// A section summoned from the hologram (radial menu or a ring node) opens as
// a floating panel over the scene, not a new page. The route still changes,
// so deep links and Back work. Not modal: Panic and the radio stay usable.

export interface SectionTab {
  to: string;
  label: string;
  icon?: Icon;
}

export function SectionPanel({ title, tabs, children, wide }: { title: string; tabs?: SectionTab[]; children: ReactNode; wide?: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();
  const headRef = useRef<HTMLHeadingElement>(null);
  const returnTo = useRef<Element | null>(null);
  useDocumentTitle(title.charAt(0).toUpperCase() + title.slice(1));

  const close = () => {
    const fromScene = (location.state as { fromScene?: boolean } | null)?.fromScene;
    if (fromScene && window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  // Land on the heading when a section opens (not on tab switches inside it).
  useEffect(() => {
    returnTo.current = document.activeElement;
    headRef.current?.focus({ preventScroll: true });
    return () => {
      const back = returnTo.current;
      // A radial menu item is gone once the menu closes; the badge that summoned it takes focus.
      const target = back instanceof HTMLElement && back.closest(".radial") ? document.querySelector<HTMLElement>(".badge") : back;
      if (target instanceof HTMLElement && target.isConnected) target.focus({ preventScroll: true });
    };
  }, []);

  return (
    <div className="section-layer">
      <div className="section-backdrop" onClick={close} aria-hidden="true" />
      <section
        className={`section${wide ? " section--wide" : ""}`}
        role="dialog"
        aria-modal="false"
        aria-labelledby="section-title"
        onKeyDown={(e) => {
          if (e.key === "Escape" && !e.defaultPrevented) {
            const t = e.target as HTMLElement;
            if (t.closest("dialog, .live, details[open]")) return;
            e.preventDefault();
            close();
          }
        }}
      >
        <i className="tick a" aria-hidden="true" />
        <i className="tick b" aria-hidden="true" />
        <i className="tick c" aria-hidden="true" />
        <i className="tick d" aria-hidden="true" />
        <header className="section__head">
          <h2 id="section-title" ref={headRef} tabIndex={-1}>
            {title}
          </h2>
          <Button iconOnly icon={XIcon} label={`Close ${title}`} className="section__close" onClick={close} />
        </header>
        {tabs ? (
          <nav className="section__tabs" aria-label={`${title} sections`}>
            {tabs.map((t) => (
              <NavLink key={t.to} to={t.to} replace state={location.state} className={({ isActive }) => btnClass({ size: "sm", variant: isActive ? "primary" : "default" }, "tab")}>
                {t.icon ? <t.icon className="btn__icon" size={14} weight="light" aria-hidden="true" /> : null}
                <span className="btn__label">{t.label}</span>
              </NavLink>
            ))}
          </nav>
        ) : null}
        <div className="section__scroll">{children}</div>
      </section>
    </div>
  );
}
